'use client';

import { useEffect, useRef } from 'react';

/**
 * Global Error Boundary (Root Layout Errors)
 *
 * Catches errors in the ROOT layout itself.
 * MUST include <html> and <body> because it replaces the entire page.
 * For route-level errors (within a layout), see error.tsx which has no html/body.
 *
 * CRITICAL: This component MUST be completely self-contained with ZERO external
 * dependencies beyond React. When the root layout crashes (e.g., chunk loading
 * failure, Sentry SDK init error, provider crash), any import from the design
 * system, Sentry, or error-reporting modules will also fail — causing this error
 * page itself to crash. That cascade creates the POST /_error 100% error rate
 * in Sentry (206 events) because Next.js falls back to the internal /_error
 * endpoint which doesn't exist in App Router projects.
 *
 * Rules:
 * - NO imports from @sentry/nextjs (use dynamic import in try-catch)
 * - NO imports from @/lib/error-reporting (heavy dependency chain)
 * - NO imports from @/lib/utils/error-logger-client (imports Sentry)
 * - NO design system components (Button, icons, etc.)
 * - ONLY inline styles (no Tailwind - stylesheet may not be loaded)
 * - ONLY native HTML elements
 * - Error reporting via dynamic import wrapped in try-catch
 *
 * @see https://nextjs.org/docs/app/api-reference/file-conventions/error#global-errorjs
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // Prevent double-capture in strict mode
  const capturedRef = useRef(false);

  useEffect(() => {
    // Report error via dynamic imports wrapped in try-catch.
    // If ANY import fails (chunk loading error, Sentry init failure, etc.),
    // the error page still renders — we just silently skip reporting.
    if (!capturedRef.current) {
      capturedRef.current = true;

      // Attempt 1: Sentry (dynamic import — does NOT block rendering)
      import('@sentry/nextjs')
        .then((Sentry) => {
          Sentry.captureException(error, {
            tags: { errorBoundary: 'global' },
            extra: { digest: error.digest },
          });
        })
        .catch(() => {
          // Sentry SDK failed to load — silently skip
        });

      // Attempt 2: Custom error reporting system (dynamic import)
      import('@/lib/error-reporting/client')
        .then(({ captureComponentError }) => {
          captureComponentError(error, undefined, {
            area: 'global',
            digest: error.digest,
          }).catch(() => {});
        })
        .catch(() => {
          // Error reporting module failed to load — silently skip
        });
    }
  }, [error]);

  return (
    <html lang="en" suppressHydrationWarning>
      <body
        suppressHydrationWarning
        style={{
          margin: 0,
          padding: 0,
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        {/* Error Card */}
        <div
          style={{
            maxWidth: '400px',
            width: '90%',
            padding: '32px',
            borderRadius: '16px',
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(212, 175, 55, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
          }}
        >
          {/* Error Icon */}
          <div
            style={{
              width: '64px',
              height: '64px',
              margin: '0 auto 24px',
              borderRadius: '50%',
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ef4444"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
          </div>

          {/* Title */}
          <h1
            style={{
              margin: '0 0 16px',
              fontSize: '24px',
              fontWeight: 'bold',
              color: '#ffffff',
            }}
          >
            Something went wrong
          </h1>

          {/* Message */}
          <p
            style={{
              margin: '0 0 8px',
              fontSize: '14px',
              color: '#9ca3af',
            }}
          >
            {error.message || 'A critical error occurred. Please try again.'}
          </p>

          {/* Error Digest */}
          {error.digest && (
            <p
              style={{
                margin: '0 0 24px',
                fontSize: '12px',
                color: '#6b7280',
                fontFamily: 'monospace',
              }}
            >
              Error ID: {error.digest}
            </p>
          )}

          {/* Buttons */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              marginTop: '24px',
            }}
          >
            <button
              onClick={reset}
              style={{
                width: '100%',
                padding: '12px 24px',
                border: 'none',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #d4af37 0%, #f5d76e 100%)',
                color: '#000000',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'opacity 0.2s',
              }}
              onMouseOver={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseOut={(e) => (e.currentTarget.style.opacity = '1')}
            >
              Try Again
            </button>

            <button
              onClick={() => (window.location.href = '/')}
              style={{
                width: '100%',
                padding: '12px 24px',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                borderRadius: '8px',
                background: 'transparent',
                color: '#d1d5db',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(212, 175, 55, 0.1)';
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.5)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(212, 175, 55, 0.3)';
              }}
            >
              Go to Home
            </button>
          </div>

          {/* Help Text */}
          <p
            style={{
              margin: '24px 0 0',
              fontSize: '12px',
              color: '#6b7280',
            }}
          >
            If this problem persists, please contact support.
          </p>
        </div>
      </body>
    </html>
  );
}
